import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  FaTachometerAlt,
  FaUsers,
  FaCalendarAlt,
  FaTaxi,
  FaMapMarkerAlt,
  FaBuilding,
  FaWallet,
  FaPlane,
  FaClock,
  FaTicketAlt,
  FaChartBar,
  FaUserCheck,
  FaUserPlus,
  FaStore,
  FaPercentage,
  FaCog,
  FaComments,
  FaFileAlt,
  FaRoute,
  FaGlobe,
  FaMobileAlt,
  FaImage,
  FaNewspaper,
  FaWrench,
  FaExclamationTriangle,
} from "react-icons/fa";

const HomeDirectory = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const userEmail = user?.data?.email || user?.user?.email || user?.email;
  const isMainAdmin = userEmail === "adarshkumar200201@gmail.com" || 
                      (userEmail && /^nihil\.khagram\d*@gmail\.com$/.test(userEmail)) ||
                      user?.isMainAdmin || 
                      user?.data?.isMainAdmin || 
                      user?.user?.isMainAdmin;
  const userRole = user?.data?.role || user?.user?.role || user?.role || "user";

  // List of all routes matched with categories
  const categories = [
    {
      title: "Dashboard & Live Operations",
      color: "from-blue-500 to-indigo-600",
      items: [
        { path: "/admin/dashboard", name: "Dashboard", desc: "Overview of system status, metrics, and summary statistics.", icon: <FaTachometerAlt /> },
        { path: "/admin/live-tracking", name: "Live Tracking", desc: "Track active rides and drivers on the live map.", icon: <FaMapMarkerAlt /> },
        { path: "/admin/ride-issues", name: "Ride Issues", desc: "Review and resolve active customer ride emergencies and problems.", icon: <FaExclamationTriangle className="text-red-500" /> },
        { path: "/admin/bookings", name: "Bookings", desc: "Manage all customer rides, schedules, and active assignments.", icon: <FaCalendarAlt /> },
        { path: "/admin/abandoned-bookings", name: "Booking Inquiries", desc: "Track dropped-off customer bookings and recover leads via WhatsApp or Call.", icon: <FaClock className="text-amber-500" /> },
      ]
    },
    {
      title: "User & Staff Management",
      color: "from-teal-500 to-emerald-600",
      items: [
        { path: "/admin/users", name: "Users", desc: "View and edit customer database profiles and transaction status.", icon: <FaUsers /> },
        { path: "/admin/roles", name: "Role Management", desc: "Configure and manage admin panel system roles.", icon: <FaUsers />, systemOnly: true },
        { path: "/admin/role-permissions", name: "Role Permissions", desc: "Assign custom module access permissions to roles.", icon: <FaUserCheck />, systemOnly: true },
        { path: "/admin/create-staff", name: "Create Staff", desc: "Add new support staff and operations accounts.", icon: <FaUserPlus />, systemOnly: true },
      ]
    },
    {
      title: "Corporate & Vendor Operations",
      color: "from-purple-500 to-pink-600",
      items: [
        { path: "/admin/corporates", name: "Corporates", desc: "Manage corporate clients and company registrations.", icon: <FaBuilding /> },
        { path: "/admin/corporate-wallet", name: "Corporate Wallet", desc: "Handle credit lines, billing, and corporate balances.", icon: <FaWallet /> },
        { path: "/admin/vendors", name: "Vendors", desc: "Manage independent fleet operators and vehicle partners.", icon: <FaStore /> },
        { path: "/admin/commission", name: "Commission", desc: "Set default commission rates for rides and vendor contracts.", icon: <FaPercentage /> },
      ]
    },
    {
      title: "Fleet & Resource Controls",
      color: "from-amber-500 to-orange-600",
      items: [
        { path: "/admin/cabs", name: "Cab Management", desc: "Register vehicles, assign models, and manage fleet details.", icon: <FaTaxi /> },
        { path: "/admin/drivers", name: "Driver Management", desc: "Approve driver profiles, verify documents, and view status.", icon: <FaUsers /> },
        { path: "/admin/airports", name: "Airport Management", desc: "Configure airport geofences and city pricing matrices.", icon: <FaPlane /> },
        { path: "/admin/rental-packages", name: "Rental Packages", desc: "Setup hourly and kilometer package limits.", icon: <FaClock /> },
      ]
    },
    {
      title: "Financials & Wallets",
      color: "from-rose-500 to-red-600",
      items: [
        { path: "/admin/wallet/summary", name: "Wallet Summary", desc: "Aggregated wallet system audits and transaction logs.", icon: <FaChartBar /> },
        { path: "/admin/wallet/drivers", name: "Driver Wallets", desc: "Check individual driver deposits, balances, and ledger logs.", icon: <FaUserCheck /> },
        { path: "/admin/wallet/withdrawals", name: "Pending Withdrawals", desc: "Process driver withdrawal requests and payouts.", icon: <FaWallet /> },
        { path: "/admin/wallet/user-withdrawals", name: "User Withdrawals", desc: "Review user refund request withdrawals.", icon: <FaWallet /> },
      ]
    },
    {
      title: "Content & Settings",
      color: "from-cyan-500 to-sky-600",
      items: [
        { path: "/admin/popular-routes", name: "Fixed Routes", desc: "Configure flat rate outstation trip pricing.", icon: <FaRoute /> },
        { path: "/admin/popular-cities", name: "Popular Cities", desc: "Manage cities highlighted on the client application.", icon: <FaGlobe /> },
        { path: "/admin/footer-routes", name: "Premium Routes", desc: "Add premium routes and descriptive packages.", icon: <FaRoute /> },
        { path: "/admin/document-settings", name: "Documents Requirements", desc: "Set verification file demands for drivers.", icon: <FaFileAlt /> },
        { path: "/admin/booking-settings", name: "Booking Settings", desc: "Manage ride search timings and default buffers.", icon: <FaCog /> },
        { path: "/admin/coupons", name: "Coupon Management", desc: "Create discount coupon codes and promo rules.", icon: <FaTicketAlt /> },
        { path: "/admin/testimonials", name: "Testimonials", desc: "Edit client reviews shown on front page.", icon: <FaComments /> },
        { path: "/admin/media", name: "Media & News", desc: "Publish updates and press releases.", icon: <FaFileAlt /> },
        { path: "/admin/blogs", name: "Blogs", desc: "Draft and manage articles for SEO reach.", icon: <FaNewspaper /> },
        { path: "/admin/ads", name: "Ads Management", desc: "Setup promotional ads shown inside mobile app.", icon: <FaImage /> },
        { path: "/admin/support-details", name: "Support Details", desc: "Configure helpline details and contacts.", icon: <FaComments /> },
        { path: "/admin/support-messages", name: "Support Messages", desc: "Read and reply to user helpline tickets.", icon: <FaComments /> },
        { path: "/admin/maintenance", name: "Maintenance Mode", desc: "Toggle portal maintenance blocks.", icon: <FaWrench /> },
        { path: "/admin/auth-images", name: "Auth Page Images", desc: "Customize splash images on login forms.", icon: <FaImage /> },
        { path: "/admin/app-version", name: "App Version", desc: "Configure minimum required Android/iOS versions.", icon: <FaMobileAlt /> },
      ]
    }
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-800 rounded-3xl p-8 md:p-12 text-white shadow-2xl relative overflow-hidden mb-10 group">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3 group-hover:scale-110 transition-transform duration-500"></div>
        <div className="relative z-10 max-w-3xl">
          <span className="bg-white/20 backdrop-blur-md px-3.5 py-1.5 rounded-full text-xs font-black uppercase tracking-wider">
            Operational Directory
          </span>
          <h1 className="text-3xl md:text-5xl font-black tracking-tight mt-4 mb-2">
            Welcome to Admin Directory
          </h1>
          <p className="text-white/80 text-sm md:text-base leading-relaxed font-medium">
            Explore all available administrative screens and system management tabs below. Use this directory to navigate quickly across modules.
          </p>
        </div>
      </div>

      {/* Categories Loop */}
      <div className="space-y-12">
        {categories.map((cat, idx) => {
          // Filter items based on role permission
          const visibleItems = cat.items.filter(item => {
            if (item.systemOnly && !isMainAdmin && userRole !== "admin") {
              return false;
            }
            return true;
          });

          if (visibleItems.length === 0) return null;

          return (
            <div key={idx} className="space-y-5">
              <h2 className="text-xl font-black text-slate-800 tracking-tight flex items-center gap-3">
                <span className={`w-3.5 h-3.5 rounded-full bg-gradient-to-r ${cat.color} shadow-sm`}></span>
                {cat.title}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visibleItems.map((item, itemIdx) => (
                  <Link
                    key={itemIdx}
                    to={item.path}
                    className="group bg-white hover:bg-slate-50 rounded-2xl border border-slate-100 hover:border-slate-200/80 shadow-sm hover:shadow-md p-5 flex gap-4 transition-all duration-200"
                  >
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${cat.color} flex items-center justify-center text-white text-lg shadow-md shadow-slate-100 group-hover:scale-105 transition-transform`}>
                      {item.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                        {item.name}
                      </h3>
                      <p className="text-xs text-slate-500 font-medium leading-relaxed mt-1">
                        {item.desc}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HomeDirectory;
